// Copyright (c) Docugami, Inc. All rights reserved.

import { ColDef, ColGroupDef, ColumnMovedEvent, GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ParentLimitsContainer } from 'components/common/LimitsContainer';
import AgGridDocumentTitleColumnHeader from 'components/layout/AgGridDocumentTitleColumnHeader';
import AgGridEntityColumnHeader from 'components/layout/AgGridEntityColumnHeader';
import AgGridGrid, { AGGridGridCellRendererProps, AgGridGridColumns } from 'components/layout/AgGridGrid';
import AgGridGroupedColumnHeader from 'components/layout/AgGridGroupedColumnHeader';
import { OneClickCoachmarkModal } from 'components/reports/OneClickCoachmarkModal';
import { GridCell } from 'components/reports/ReportGridContentCell';
import { selectFilteredReportGridDocuments, selectFilteredReportGridEntries } from 'components/work_path/ReportDesignerCanvas';
import { NavigationType, ReportViewMode, useNavigationService } from 'hooks/navigationService';
import { useAppSelector } from 'hooks/redux';
import { useReportEntryActions } from 'hooks/reportEntryActions';
import { useWaitCursor } from 'hooks/WaitCursor';
import { DocumentEvaluationStatus, ReportEntry, ReportViewContext } from 'networking/reportsApi';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRemoveEntriesMutation } from 'store/reports/reportsSlice';
import { createSelectDocumentsNeedingRepublishing, createSelectDocumentsWithCpml, createSelectGridEntryGroups, createSelectInProgressDocumentsForAutoConfirm, selectIsReportPublished, selectIsReportSchemaLoaded, selectReportContext, selectReportDocumentsView, selectReportId, selectReportSchema } from 'store/reports/selectors';
import styled from 'styled-components';
import { RemoveEntriesSchema, ReportFileNameClickedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { AutomationId } from 'utils/Automation';
import { getShowFirstOneClickWorkPathViewingUx, setShowFirstOneClickWorkPathViewingUx } from 'utils/localStorageHelpers';
import { useReportColumnDragTool } from './reportColumnDragTool';
import { DocumentTitleCell } from './ReportGridDocumentTitleCell';

const Root = styled.div`
    position: relative;
    flex: 1;
`;

const docNameColumnKey = 'document_name';

export interface IReportRow {
    index: number | undefined;
    folderId: string;
    fileName: string;
    isDisabled: boolean;
    statusMessage: string;
    documentNeedsRepublishing: boolean;
    isPublishing: boolean;
    isLoading?: boolean;
}

const selectDocumentsNeedingRepublishing = createSelectDocumentsNeedingRepublishing();
const selectInProgressDocumentsForAutoConfirm = createSelectInProgressDocumentsForAutoConfirm();
const selectEntryGroupsSchema = createSelectGridEntryGroups();
export const selectDocumentsWithCpml = createSelectDocumentsWithCpml();

export interface TableViewProps {
    className?: string;
}

export const AgGridReportTable = (props: TableViewProps): JSX.Element => {
    const navService = useNavigationService();

    const currentWorkPathId = useAppSelector(state => state.workPaths.currentWorkPath?.id ?? '');

    const reportId = useAppSelector(selectReportId);
    const isSchemaLoaded = useAppSelector(selectIsReportSchemaLoaded);
    const documentsView = useAppSelector(selectReportDocumentsView);
    const isPublished = useAppSelector(selectIsReportPublished);
    const context = useAppSelector(selectReportContext);
    const schema = useAppSelector(selectReportSchema);

    const filterDocIds = useAppSelector(selectFilteredReportGridDocuments);
    const reportEntries: ReportEntry[] = useAppSelector(state => selectFilteredReportGridEntries(state, reportId, context));

    const showFirstWorkPathViewingUx = getShowFirstOneClickWorkPathViewingUx(currentWorkPathId);
    const [showRemoveAllModal, setShowRemoveAllModal] = useState(false);

    const entryIds = useMemo(() => reportEntries.map(x => x.id), [reportEntries]);

    const columnGroups = useAppSelector(state => selectEntryGroupsSchema(state, reportId, reportEntries));
    const documentsNeedingRepublishing = useAppSelector(state => selectDocumentsNeedingRepublishing(state, reportId));
    const inProgressDocuments = useAppSelector(state => selectInProgressDocumentsForAutoConfirm(state, reportId, schema?.report.clusterId || ''));
    const isLoading = !isSchemaLoaded || !documentsView;

    const [triggerRemove, removeState] = useRemoveEntriesMutation();
    const { canRemoveEntry } = useReportEntryActions();

    useWaitCursor(removeState.isLoading);

    const reportColumnDragTool = useReportColumnDragTool();

    const gridRef: MutableRefObject<AgGridReact<IReportRow> | undefined> = useRef<AgGridReact<IReportRow>>();

    const onRemoveAllEntries = useCallback(() => {
        const entryIds = reportEntries.map(entry => entry.id);

        triggerRemove({ reportId: reportId, entryIds: entryIds });
        Telemetry.trackEvent<RemoveEntriesSchema>({
            reportId: reportId,
            entryIds: entryIds,
            isRemoveAll: true,
            eventName: TelemetryEvent.RemoveEntries
        })
    }, [reportEntries, reportId, triggerRemove]);

    const onRemoveEntry = useCallback((entry: ReportEntry) => {
        const entryIds = [entry!.id];
        triggerRemove({ reportId: entry!.reportId, entryIds: entryIds });
        Telemetry.trackEvent<RemoveEntriesSchema>({
            reportId: reportId,
            entryIds: entryIds,
            eventName: TelemetryEvent.RemoveEntries,
            isRemoveAll: false
        })
    }, [reportId, triggerRemove]);

    const getDocStatusIcon = useCallback((documentId: string): string => {
        if (!!documentsNeedingRepublishing?.[documentId]) {
            return 'Error'
        }

        return '';
    }, [documentsNeedingRepublishing])

    useEffect(() => {
        if (!!schema?.entries.ids.length && showFirstWorkPathViewingUx) {
            setShowRemoveAllModal(true);
        }

    }, [schema?.entries.ids.length, showFirstWorkPathViewingUx])

    const getDocStatusMessage = useCallback((documentId: string): string => {
        const document = documentsNeedingRepublishing?.[documentId];
        switch (document?.evaluationStatus) {
            case DocumentEvaluationStatus.Confirmed:
                return 'This doc is currently publishing. Please wait until it has finished publishing to access it in the report.'
            default:
                return document?.reason || '';
        }
    }, [documentsNeedingRepublishing])

    const navToDocument = useCallback((folderId: string, chunkInstanceId?: string): void => {
        navService.navToReportPage(
            NavigationType.ReplaceCurrentPage,
            {
                mode: ReportViewMode.Document,
                documentId: folderId,
                instanceId: chunkInstanceId
            });
    }, [navService]);

    const gridRows = useMemo((): IReportRow[] => {
        if (!documentsView) {
            return [];
        }

        const docIds = filterDocIds || documentsView.ids;

        const rows = docIds.reduce<IReportRow[]>((rows, docId) => {
            const reportDocument = documentsView.entities[docId];
            if (!!reportDocument) {
                const evaluationStatus = reportDocument.state.evaluationStatus;
                const isPublishing = evaluationStatus === DocumentEvaluationStatus.Confirmed;
                rows.push({
                    index: reportDocument.index,
                    folderId: docId,
                    fileName: reportDocument.clusterDocument.filename,
                    isDisabled: !reportDocument.clusterDocument.hasCpml ||
                        isPublishing,
                    statusMessage: getDocStatusMessage(docId),
                    isPublishing,
                    documentNeedsRepublishing: !!documentsNeedingRepublishing?.[docId]
                })
            }
            return rows;
        }, [] as IReportRow[]);

        if (!filterDocIds) {
            inProgressDocuments.forEach(inProgressDoc => {
                if (!docIds.includes(inProgressDoc.folderId)) {
                    rows.push({
                        index: undefined,
                        folderId: inProgressDoc.folderId,
                        fileName: inProgressDoc.filename,
                        isDisabled: true,
                        statusMessage: 'Document is processing, please wait while we load the data',
                        isPublishing: false,
                        documentNeedsRepublishing: false,
                        isLoading: true
                    })
                }
            })
        }

        return rows;
    }, [documentsView, filterDocIds, getDocStatusMessage, documentsNeedingRepublishing, inProgressDocuments])

    const onFileNameClicked = useCallback((folderId: string) => {
        Telemetry.trackEvent<ReportFileNameClickedSchema>({
            folderId: folderId,
            eventName: TelemetryEvent.ReportFileNameClicked
        });
        navToDocument(folderId);
    }, [navToDocument]);

    async function onColumnReorder(e: ColumnMovedEvent<IReportRow>) {
        if (!schema) {
            return;
        }
        const { report } = schema;

        reportColumnDragTool.handleMovement({
            event: e,
            reportId,
            report,
            columns,
            entries: schema.entries,
            isLoading,
            isPublished: isPublished || false,
        });
    }

    const columns = useMemo(() => {
        const width = 250;

        const showConfirmAll = context === ReportViewContext.Review;

        const createDocumentNameColumnDef = () => ({
            keyCreator: () => docNameColumnKey,
            minWidth: width,
            maxWidth: undefined,
            resizable: false,
            headerName: 'Document',
            headerComponent: AgGridDocumentTitleColumnHeader,
            headerComponentParams: {
                reportId,
                onRemoveAllEntries,
                canRemoveAllEntries: canRemoveEntry && !!reportEntries.length,
            },
            suppressMovable: true,
            lockPosition: 'left',
            pinned: 'left',
            cellRenderer: (props: AGGridGridCellRendererProps) => {
                const item = props.data;
                if (!item) {
                    return null;
                }

                const indexStr = item.index !== undefined ? `${item.index + 1}. ` : '';

                return (
                    <DocumentTitleCell
                        showDocumentConfirmation={showConfirmAll}
                        folderId={item.folderId}
                        fileName={`${indexStr}${item.fileName}`}
                        isDisabled={item.isDisabled}
                        statusMessage={item.statusMessage}
                        icon={getDocStatusIcon(item.folderId)}
                        onClick={() => onFileNameClicked(item.folderId)}
                        canRepublish={item.documentNeedsRepublishing}
                        isPublishing={item.isPublishing}
                        entryIds={entryIds}
                        onResize={(newHeight) => {
                            if (gridRef.current) {
                                const gridApi = (gridRef.current as any).api as GridApi;
                                if (!gridApi) {
                                    return;
                                }

                                const rowNode = gridApi.getRowNode(`${item.folderId}-${item.fileName}`);
                                if (rowNode) {
                                    rowNode.setRowHeight(newHeight);
                                    gridApi.onRowHeightChanged();
                                }
                            }
                        }}
                    />
                )
            },
        } as ColDef<IReportRow>);

        const entryColumns: AgGridGridColumns = [createDocumentNameColumnDef()];

        reportEntries.forEach((entry, index) => {
            const group = columnGroups.find(x => x.key === entry.groupId);

            const width = 320;

            if (group) {
                let existingGroup: ColGroupDef<IReportRow> = entryColumns.find(col => (col as ColGroupDef<IReportRow>).groupId === group.key) as ColGroupDef<IReportRow>;
                if (!existingGroup) {
                    existingGroup = {
                        groupId: group.key,
                        headerName: group.name,
                        children: [],
                        marryChildren: false,
                        headerGroupComponent: AgGridGroupedColumnHeader,
                        headerGroupComponentParams: {
                            group,
                        },
                    };

                    entryColumns.push(existingGroup);
                }

                existingGroup.children.push({
                    keyCreator: () => entry.id,
                    colId: entry.id,
                    field: undefined,
                    minWidth: width,
                    maxWidth: undefined,
                    resizable: false,
                    suppressMovable: isPublished,
                    headerName: entry.title,
                    lockPinned: true,
                    headerComponent: AgGridEntityColumnHeader,
                    headerComponentParams: {
                        entryIndex: index,
                        canRemoveEntry,
                        onRemoveEntry,
                        showEntryConfirmation: showConfirmAll,
                        isLocked: isPublished,
                        entry,
                        columnGroups
                    },
                    cellRenderer: (props: AGGridGridCellRendererProps) => {
                        const item = props.data;

                        if (!item) {
                            return null;
                        }

                        return (
                            <GridCell
                                automationId={AutomationId.WorkPathGridDocumentEntryValueCell}
                                entry={entry}
                                folderId={item.folderId}
                                fileName={item.fileName}
                                onClick={navToDocument}
                                isLoading={!!isLoading}
                                disabled={item.isDisabled}
                                isGrouped={!!group} />
                        )
                    },
                } as ColDef<IReportRow>);
            } else {
                entryColumns.push({
                    keyCreator: () => entry.id,
                    colId: entry.id,
                    field: undefined,
                    minWidth: width,
                    maxWidth: undefined,
                    resizable: false,
                    suppressMovable: isPublished,
                    headerName: entry.title,
                    lockPinned: true,
                    headerComponent: AgGridEntityColumnHeader,
                    headerComponentParams: {
                        entryIndex: index,
                        canRemoveEntry,
                        onRemoveEntry,
                        showEntryConfirmation: showConfirmAll,
                        isLocked: isPublished,
                        entry,
                        columnGroups
                    },
                    cellRenderer: (props: AGGridGridCellRendererProps) => {
                        const item = props.data;

                        if (!item) {
                            return null;
                        }

                        return (
                            <GridCell
                                automationId={AutomationId.WorkPathGridDocumentEntryValueCell}
                                entry={entry}
                                folderId={item.folderId}
                                fileName={item.fileName}
                                onClick={navToDocument}
                                isLoading={!!isLoading}
                                disabled={item.isDisabled}
                                isGrouped={!!group} />
                        )
                    },
                });
            }
        });

        return entryColumns;
    }, [reportId, context, reportEntries, onRemoveAllEntries, canRemoveEntry, getDocStatusIcon, onFileNameClicked, columnGroups, onRemoveEntry, isPublished, navToDocument, isLoading, entryIds]);

    return (
        <>
            <OneClickCoachmarkModal
                numberOfEntries={reportEntries.length}
                hidden={!showRemoveAllModal}
                onDismiss={() => {
                    setShowRemoveAllModal(false);
                    setShowFirstOneClickWorkPathViewingUx(currentWorkPathId, false);
                }}
            />
            <Root className={props.className}>
                <ParentLimitsContainer>
                    <AgGridGrid
                        gridRef={gridRef as MutableRefObject<AgGridReact<IReportRow>>}
                        columns={columns}
                        items={gridRows}
                        onColumnMoved={onColumnReorder}
                    />
                </ParentLimitsContainer>
            </Root>
        </>
    );
}

